import { useProductNavigation } from '@components/utils/contexts/ProductNavigationContext';
import { ModuleFragment } from '@graphql/generated/graphql';
import { OrderedModule } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useDebounce, useScroll } from 'react-use';

type Props = {
    reverse?: boolean;
    module?: OrderedModule | ModuleFragment;
    scrollOptions?: ScrollIntoViewOptions;
};

export const useContentScroller = ({
    reverse = false,
    module,
    scrollOptions,
}: Props) => {
    const parentRef = useRef<HTMLUListElement>(null);
    const childrenRefs = useRef<HTMLLIElement[]>([]);
    const [allowForth, setAllowForth] = useState<boolean>(true);
    const [allowBack, setAllowBack] = useState<boolean>(false);
    const { maybeRestoreHorizontalNavigation } = useProductNavigation();

    useEffect(() => {
        if (module) {
            maybeRestoreHorizontalNavigation(parentRef, module);
        }

        return () => {
            childrenRefs.current = [];
        };
    }, [maybeRestoreHorizontalNavigation, module]);

    useDebounce(
        () => {
            if (reverse) {
                setAllowForth(
                    (parentRef.current &&
                        parentRef.current.scrollLeft >
                            1 -
                                parentRef.current.scrollWidth +
                                parentRef.current.clientWidth) ??
                        false
                );
                setAllowBack(
                    (parentRef.current && parentRef.current.scrollLeft < 0) ??
                        false
                );
            } else {
                setAllowForth(
                    (parentRef.current &&
                        parentRef.current.scrollLeft <
                            parentRef.current.scrollWidth -
                                parentRef.current.clientWidth -
                                1) ??
                        false
                );
                setAllowBack(
                    (parentRef.current && parentRef.current.scrollLeft > 0) ??
                        false
                );
            }
        },
        250,
        [useScroll(parentRef)]
    );

    const handleBack = () => {
        try {
            const targetEl = childrenRefs.current
                .filter(
                    (ref) =>
                        parentRef.current &&
                        (reverse
                            ? ref.getBoundingClientRect().right >
                              parentRef.current.getBoundingClientRect().right
                            : ref.getBoundingClientRect().left <
                              parentRef.current.getBoundingClientRect().left)
                )
                .slice(-1)[0];
            targetEl &&
                targetEl.scrollIntoView(
                    scrollOptions ?? {
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'end',
                    }
                );
        } catch (e) {
            console.error(e);
        }
    };

    const handleForth = () => {
        try {
            childrenRefs.current.every((ref) => {
                if (
                    parentRef.current &&
                    (reverse
                        ? ref.getBoundingClientRect().left <
                          parentRef.current.getBoundingClientRect().left
                        : ref.getBoundingClientRect().right >
                          parentRef.current.getBoundingClientRect().right)
                ) {
                    ref.scrollIntoView(
                        scrollOptions ?? {
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'start',
                        }
                    );
                    return false;
                }
                return true;
            });
        } catch (e) {
            console.error(e);
        }
    };

    return {
        parentRef,
        childrenRefs,
        allowBack,
        allowForth,
        handleBack,
        handleForth,
    };
};
